import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { sellersAPI, followsAPI } from '../../services/api';
import { messagingService } from '../../services/messagingService';
import {
  Search,
  Building2,
  MapPin,
  FlaskConical,
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  List,
  X,
  Loader2,
  MessageSquare,
  Sparkles,
  ArrowRight,
  Heart,
  Filter,
} from 'lucide-react';

// Modality options - most relevant for pharma BD
const MODALITIES = [
  'ADC', 'CAR-T', 'Small Molecule', 'Bispecific', 'mRNA',
  'Gene Therapy', 'Antibody', 'Cell Therapy', 'Radioligand'
];

// Stage options with colors
const STAGES = [
  { value: 'Preclinical', color: 'bg-slate-100 text-slate-700' },
  { value: 'Phase 1', color: 'bg-blue-100 text-blue-700' },
  { value: 'Phase 2', color: 'bg-amber-100 text-amber-700' },
  { value: 'Phase 3', color: 'bg-orange-100 text-orange-700' },
  { value: 'Approved', color: 'bg-emerald-100 text-emerald-700' },
];

// Therapeutic areas
const THERAPEUTIC_AREAS = [
  'Oncology', 'Immunology', 'Neuroscience', 'Cardiovascular',
  'Rare Disease', 'Infectious Disease', 'Metabolic', 'Ophthalmology'
];

// Deal types
const DEAL_TYPES = [
  'License', 'Co-development', 'Acquisition', 'Option'
];

// Geography
const GEOGRAPHIES = [
  'Greater China', 'North America', 'Europe', 'Japan', 'Global'
];

export default function BuyerSearch() {
  const { userProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isBuyer = userProfile?.user_type === 'buyer';
  const isInBuyerLayout = location.pathname.startsWith('/buyer/');

  const [loading, setLoading] = useState(true);
  const [sellers, setSellers] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [followedSellers, setFollowedSellers] = useState(new Set());
  const [followLoading, setFollowLoading] = useState(null);
  const [messageLoading, setMessageLoading] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    totalCount: 0,
    totalPages: 0,
  });

  const [filters, setFilters] = useState({
    search: '',
    modalities: [],
    stages: [],
    therapeuticAreas: [],
    dealTypes: [],
    geographies: [],
  });

  // Load followed sellers on mount
  useEffect(() => {
    if (isBuyer) {
      loadFollowedSellers();
    }
  }, [isBuyer]);

  const loadFollowedSellers = async () => {
    try {
      const response = await followsAPI.getFollowed();
      const followed = response.data?.data || [];
      setFollowedSellers(new Set(followed.map(f => f.seller_id)));
    } catch (error) {
      console.debug('Failed to load followed sellers:', error);
    }
  };

  useEffect(() => {
    loadSellers();
  }, [pagination.page]);

  const loadSellers = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search || undefined,
        modality: filters.modalities.length === 1 ? filters.modalities[0] : undefined,
        stage: filters.stages.length === 1 ? filters.stages[0] : undefined,
      };
      Object.keys(params).forEach(
        (key) => params[key] === undefined && delete params[key]
      );

      const response = await sellersAPI.getAll(params);
      setSellers(response.data?.data?.sellers || []);
      setPagination((prev) => ({
        ...prev,
        ...response.data?.data?.pagination,
      }));
    } catch (error) {
      console.error('Failed to load sellers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    loadSellers();
  };

  const toggleFilter = (category, value) => {
    setFilters(prev => {
      const current = prev[category];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [category]: updated };
    });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      modalities: [],
      stages: [],
      therapeuticAreas: [],
      dealTypes: [],
      geographies: [],
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const toggleCompanySelection = (id) => {
    setSelectedCompanies(prev =>
      prev.includes(id)
        ? prev.filter(c => c !== id)
        : [...prev, id]
    );
  };

  const handleFollow = async (sellerId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isBuyer || followLoading) return;

    setFollowLoading(sellerId);
    try {
      const isFollowing = followedSellers.has(sellerId);
      if (isFollowing) {
        await followsAPI.unfollow(sellerId);
        setFollowedSellers(prev => {
          const next = new Set(prev);
          next.delete(sellerId);
          return next;
        });
      } else {
        await followsAPI.follow(sellerId);
        setFollowedSellers(prev => new Set(prev).add(sellerId));
      }
    } catch (error) {
      console.error('Failed to update follow:', error);
    } finally {
      setFollowLoading(null);
    }
  };

  const handleMessage = async (sellerId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isBuyer || messageLoading) return;

    setMessageLoading(sellerId);
    try {
      const buyerId = userProfile?.buyer_id;
      if (!buyerId) return;

      const conversation = await messagingService.getOrCreateConversation(sellerId, buyerId);
      navigate(`/buyer/messages?conversation=${conversation.id}`);
    } catch (error) {
      console.error('Failed to start conversation:', error);
    } finally {
      setMessageLoading(null);
    }
  };

  const activeFilterCount = [
    ...filters.modalities,
    ...filters.stages,
    ...filters.therapeuticAreas,
    ...filters.dealTypes,
    ...filters.geographies,
  ].length;

  const companyDetailPath = (id) => isInBuyerLayout ? `/buyer/company/${id}` : `/company/${id}`;

  return (
    <div className={isInBuyerLayout ? '' : 'min-h-screen bg-gray-50'}>
      {/* Header - Only show if not in buyer layout */}
      {!isInBuyerLayout && (
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center overflow-hidden">
                  <img
                    src="/logo.png"
                    alt="Pharmunity"
                    className="h-12 w-12 object-cover scale-[1.35]"
                  />
                </div>
                <span className="text-xl font-bold text-gray-900">Pharmunity</span>
              </Link>
              <Link to="/login" className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">
                Sign In
              </Link>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <div className={isInBuyerLayout ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'}>
        {/* Title Bar */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Find Opportunities</h1>
            <p className="text-sm text-gray-500 mt-1">
              {pagination.totalCount} biotech companies seeking partnerships
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* AI Matches Link */}
            {isBuyer && (
              <Link
                to="/buyer/matches"
                className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-sm font-medium hover:from-amber-600 hover:to-orange-600 transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                Smart Matches
              </Link>
            )}

            {/* View Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded ${viewMode === 'table' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
                title="Table view"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded ${viewMode === 'cards' ? 'bg-white shadow-sm' : 'text-gray-500'}`}
                title="Card view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-xl border border-gray-200 mb-6">
          <form onSubmit={handleSearch} className="p-4">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Search by company, target, indication, modality..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button type="submit" className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700">
                Search
              </button>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border font-medium transition-colors ${
                  showFilters || activeFilterCount > 0
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Filters</span>
                {activeFilterCount > 0 && (
                  <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </form>

          {/* Quick Filter Pills - Modalities & Stages */}
          <div className="px-4 pb-3 flex flex-wrap gap-2">
            {/* Stage Pills First - Most Important for BD */}
            {STAGES.map(({ value, color }) => (
              <button
                key={value}
                onClick={() => toggleFilter('stages', value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  filters.stages.includes(value)
                    ? 'bg-blue-600 text-white shadow-sm'
                    : `${color} hover:opacity-80`
                }`}
              >
                {value}
              </button>
            ))}
            <span className="text-gray-300">|</span>
            {/* Modality Pills */}
            {MODALITIES.slice(0, 5).map(mod => (
              <button
                key={mod}
                onClick={() => toggleFilter('modalities', mod)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  filters.modalities.includes(mod)
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {mod}
              </button>
            ))}
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="border-t border-gray-100 p-4 bg-gray-50">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {/* Therapeutic Area */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Therapeutic Area
                  </label>
                  <div className="space-y-1.5">
                    {THERAPEUTIC_AREAS.map(area => (
                      <label key={area} className="flex items-center gap-2 text-sm cursor-pointer hover:text-blue-600">
                        <input
                          type="checkbox"
                          checked={filters.therapeuticAreas.includes(area)}
                          onChange={() => toggleFilter('therapeuticAreas', area)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        {area}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Deal Type */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Deal Type
                  </label>
                  <div className="space-y-1.5">
                    {DEAL_TYPES.map(type => (
                      <label key={type} className="flex items-center gap-2 text-sm cursor-pointer hover:text-blue-600">
                        <input
                          type="checkbox"
                          checked={filters.dealTypes.includes(type)}
                          onChange={() => toggleFilter('dealTypes', type)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        {type}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Geography */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Territory Rights
                  </label>
                  <div className="space-y-1.5">
                    {GEOGRAPHIES.map(geo => (
                      <label key={geo} className="flex items-center gap-2 text-sm cursor-pointer hover:text-blue-600">
                        <input
                          type="checkbox"
                          checked={filters.geographies.includes(geo)}
                          onChange={() => toggleFilter('geographies', geo)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        {geo}
                      </label>
                    ))}
                  </div>
                </div>

                {/* All Modalities */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Modalities
                  </label>
                  <div className="space-y-1.5">
                    {MODALITIES.slice(5).map(mod => (
                      <label key={mod} className="flex items-center gap-2 text-sm cursor-pointer hover:text-blue-600">
                        <input
                          type="checkbox"
                          checked={filters.modalities.includes(mod)}
                          onChange={() => toggleFilter('modalities', mod)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        {mod}
                      </label>
                    ))}
                  </div>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={clearFilters}
                      className="mt-4 text-sm text-blue-600 hover:underline"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
              <p className="text-gray-500">Loading opportunities...</p>
            </div>
          </div>
        ) : sellers.length > 0 ? (
          <>
            {viewMode === 'table' ? (
              /* Table View */
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="w-8 px-4 py-3">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600"
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCompanies(sellers.map(s => s.id));
                            } else {
                              setSelectedCompanies([]);
                            }
                          }}
                          checked={selectedCompanies.length === sellers.length && sellers.length > 0}
                        />
                      </th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                        Company
                      </th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                        Stage
                      </th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                        Modality
                      </th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                        Pipeline
                      </th>
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                        Location
                      </th>
                      <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 w-32">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sellers.map((seller) => (
                      <tr
                        key={seller.id}
                        className="hover:bg-blue-50/50 transition-colors cursor-pointer group"
                        onClick={() => navigate(companyDetailPath(seller.id))}
                      >
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-blue-600"
                            checked={selectedCompanies.includes(seller.id)}
                            onChange={() => toggleCompanySelection(seller.id)}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                              {seller.company_name?.substring(0, 2).toUpperCase() || 'CO'}
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                                {seller.company_name}
                              </div>
                              {seller.company_name_cn && (
                                <div className="text-xs text-gray-400 truncate">{seller.company_name_cn}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(seller.stages || []).filter(Boolean).slice(0, 2).map((stage) => {
                              const stageConfig = STAGES.find(s => s.value === stage);
                              return (
                                <span
                                  key={stage}
                                  className={`px-2 py-0.5 text-xs rounded-full ${stageConfig?.color || 'bg-gray-100 text-gray-700'}`}
                                >
                                  {stage}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(seller.modalities || []).filter(Boolean).slice(0, 2).map((mod) => (
                              <span
                                key={mod}
                                className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded"
                              >
                                {mod}
                              </span>
                            ))}
                            {(seller.modalities?.length || 0) > 2 && (
                              <span className="text-xs text-gray-400">+{seller.modalities.length - 2}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <FlaskConical className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{seller.asset_count || 0}</span>
                            <span className="text-gray-400">assets</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <MapPin className="w-3.5 h-3.5 text-gray-400" />
                            <span className="truncate max-w-[100px]">{seller.headquarters || '-'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-1">
                            {isBuyer && (
                              <>
                                <button
                                  onClick={(e) => handleFollow(seller.id, e)}
                                  className={`p-1.5 rounded-lg transition-colors ${
                                    followedSellers.has(seller.id)
                                      ? 'bg-pink-100 text-pink-600'
                                      : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                                  }`}
                                  title={followedSellers.has(seller.id) ? 'Remove from watchlist' : 'Add to watchlist'}
                                >
                                  {followLoading === seller.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Heart className={`w-4 h-4 ${followedSellers.has(seller.id) ? 'fill-current' : ''}`} />
                                  )}
                                </button>
                                <button
                                  onClick={(e) => handleMessage(seller.id, e)}
                                  className="p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                  title="Send Message"
                                >
                                  {messageLoading === seller.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <MessageSquare className="w-4 h-4" />
                                  )}
                                </button>
                              </>
                            )}
                            <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-blue-600 transition-colors ml-1" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Card View */
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sellers.map((seller) => (
                  <Link
                    key={seller.id}
                    to={companyDetailPath(seller.id)}
                    className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg hover:border-blue-200 transition-all group"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold">
                          {seller.company_name?.substring(0, 2).toUpperCase() || 'CO'}
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                            {seller.company_name}
                          </h3>
                          {seller.headquarters && (
                            <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                              <MapPin className="w-3 h-3" />
                              {seller.headquarters}
                            </div>
                          )}
                        </div>
                      </div>
                      {isBuyer && (
                        <button
                          onClick={(e) => handleFollow(seller.id, e)}
                          className={`p-2 rounded-lg transition-colors ${
                            followedSellers.has(seller.id)
                              ? 'bg-pink-100 text-pink-600'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                          }`}
                        >
                          <Heart className={`w-4 h-4 ${followedSellers.has(seller.id) ? 'fill-current' : ''}`} />
                        </button>
                      )}
                    </div>

                    {seller.description && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{seller.description}</p>
                    )}

                    {/* Modalities */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(seller.modalities || []).filter(Boolean).slice(0, 3).map((mod) => (
                        <span key={mod} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                          {mod}
                        </span>
                      ))}
                      {(seller.modalities?.length || 0) > 3 && (
                        <span className="text-xs text-gray-400">+{seller.modalities.length - 3}</span>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <FlaskConical className="w-4 h-4" />
                        <span className="font-medium">{seller.asset_count || 0}</span> assets
                      </div>
                      <div className="flex gap-1">
                        {(seller.stages || []).filter(Boolean).slice(0, 2).map((stage) => {
                          const stageConfig = STAGES.find(s => s.value === stage);
                          return (
                            <span
                              key={stage}
                              className={`px-2 py-0.5 text-xs rounded-full ${stageConfig?.color || 'bg-gray-100 text-gray-700'}`}
                            >
                              {stage}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.totalCount)} of{' '}
                  {pagination.totalCount}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="p-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="px-4 py-2 text-sm text-gray-600">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                    disabled={pagination.page === pagination.totalPages}
                    className="p-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Selected Companies Action Bar */}
            {selectedCompanies.length > 0 && (
              <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white rounded-xl shadow-2xl px-6 py-3 flex items-center gap-4 z-50">
                <span className="text-sm">
                  <span className="font-semibold">{selectedCompanies.length}</span> selected
                </span>
                <div className="flex gap-2">
                  <button className="px-4 py-2 bg-blue-600 rounded-lg text-sm font-medium hover:bg-blue-700">
                    Compare
                  </button>
                  <button className="px-4 py-2 bg-white/10 rounded-lg text-sm font-medium hover:bg-white/20">
                    Add to Watchlist
                  </button>
                </div>
                <button
                  onClick={() => setSelectedCompanies([])}
                  className="p-1.5 hover:bg-white/10 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        ) : (
          /* Empty State */
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No companies found</h3>
            <p className="text-gray-500 mb-6 max-w-sm mx-auto">
              Try adjusting your search terms or filters to find more opportunities
            </p>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
